import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    List,
    ListItem,
    ListItemText,
    IconButton,
    Tooltip,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
} from '@mui/material';
import {
    Add as AddIcon,
    LinkOff as LinkOffIcon,
} from '@mui/icons-material';
import { fetchClientAdvisers, removeClientAdviser } from '../../services/clients.service';
import { useAlert } from '../../functions/useAlert';
import LinkAdvisersModal from './LinkAdvisersModal';
import { Adviser, AssignedAdvisersSectionProps, GetAdvisersResponse } from './types';

const AssignedAdvisersSection: React.FC<AssignedAdvisersSectionProps> = ({
    clientId,
    orgId,
    onRefresh,
}) => {
    const [advisers, setAdvisers] = useState<Adviser[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [adviserToRemove, setAdviserToRemove] = useState<Adviser | null>(null);
    const [isRemoving, setIsRemoving] = useState(false);
    const [setAlert] = useAlert();

    const fetchAdvisers = async () => {
        setIsLoading(true);
        try {
            const response = await fetchClientAdvisers(clientId) as GetAdvisersResponse;
            setAdvisers(response.advisers || []);
        } catch (error: any) {
            setAlert(error.message || 'Failed to load advisers', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (clientId) {
            fetchAdvisers();
        }
    }, [clientId]);

    const handleOpenModal = () => {
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
    };

    const handleSaveModal = () => {
        fetchAdvisers();
        if (onRefresh) {
            onRefresh();
        }
    };

    const handleRemoveClick = (adviser: Adviser) => {
        setAdviserToRemove(adviser);
    };

    const handleConfirmRemove = async () => {
        if (!adviserToRemove) return;

        setIsRemoving(true);
        try {
            await removeClientAdviser(clientId, adviserToRemove.userId);
            setAlert('Adviser removed successfully', 'success');
            fetchAdvisers();
            if (onRefresh) {
                onRefresh();
            }
        } catch (error: any) {
            setAlert(error.message || 'Failed to remove adviser', 'error');
        } finally {
            setIsRemoving(false);
            setAdviserToRemove(null);
        }
    };

    const handleCancelRemove = () => {
        setAdviserToRemove(null);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, minWidth: 0 }}>
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
                flexShrink={0}
            >
                <Typography variant="h6" noWrap>Assigned Advisers</Typography>
                <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleOpenModal}
                    sx={{ flexShrink: 0 }}
                >
                    Link
                </Button>
            </Box>

            <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1, minHeight: 0 }}>
                {isLoading ? (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        height="100%"
                    >
                        <CircularProgress />
                    </Box>
                ) : advisers.length > 0 ? (
                    <List dense sx={{ p: 0 }}>
                        {advisers.map((adviser) => (
                            <ListItem
                                key={adviser.userId}
                                secondaryAction={
                                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                                        <Tooltip title="Remove Adviser">
                                            <IconButton
                                                edge="end"
                                                size="small"
                                                onClick={() => handleRemoveClick(adviser)}
                                            >
                                                <LinkOffIcon fontSize="small" color="error" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                }
                                disablePadding
                                sx={{ pr: 6 }}
                            >
                                <ListItemText
                                    disableTypography
                                    primary={
                                        <Box display="flex" alignItems="center" gap={1} overflow="hidden">
                                            <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                                {adviser.firstName} {adviser.lastName}
                                            </Typography>
                                            <Chip
                                                label={adviser.role}
                                                size="small"
                                                variant="outlined"
                                                sx={{ height: 20, fontSize: '0.7rem', flexShrink: 0 }}
                                            />
                                        </Box>
                                    }
                                    secondary={
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            noWrap
                                            sx={{ display: 'block', mt: 0.5 }}
                                        >
                                            {adviser.email}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                ) : (
                    <Box
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        height="100%"
                    >
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            align="center"
                        >
                            No advisers assigned.
                        </Typography>
                    </Box>
                )}
            </Box>

            <LinkAdvisersModal
                open={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveModal}
                clientId={clientId}
                orgId={orgId}
                existingAdvisers={advisers}
            />

            <Dialog
                open={!!adviserToRemove}
                onClose={handleCancelRemove}
                maxWidth="xs"
            >
                <DialogTitle>Remove Adviser</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to remove{' '}
                        <strong>
                            {adviserToRemove?.firstName} {adviserToRemove?.lastName}
                        </strong>{' '}
                        from this client?
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCancelRemove} variant="outlined">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmRemove}
                        variant="contained"
                        color="error"
                        disabled={isRemoving}
                    >
                        {isRemoving ? <CircularProgress size={24} /> : 'Remove'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AssignedAdvisersSection;
